/**
 * Embedded web bundle identity + remote version.json fetch for staleness checks.
 */

import { cacheBustedReload } from './bootRecovery';
import { useMaestroStore } from '../store/maestroStore';

export interface WebBundleMeta {
  version: string;
  sha: string;
  gitHash: string;
  buildTime: string;
  /** Content-addressed entry chunk (e.g. index-Abc123.js) — release-manifest primary key. */
  bundleHash: string;
}

function readEmbeddedBundleHash(): string {
  if (typeof window === 'undefined') return '';
  try {
    const fromWindow = (window as Window & { __MAESTRO_BUNDLE_HASH__?: string }).__MAESTRO_BUNDLE_HASH__;
    if (typeof fromWindow === 'string' && fromWindow.trim()) {
      return fromWindow.trim().replace(/^assets\//, '');
    }
  } catch {
    /* ignore */
  }
  try {
    const scripts = document.querySelectorAll('script[src*="index-"]');
    for (const node of scripts) {
      const src = node.getAttribute('src') || '';
      const match = src.match(/(?:^|\/)assets\/(index-[A-Za-z0-9_-]+\.js)(?:\?|$)/)
        || src.match(/(?:^|\/)(index-[A-Za-z0-9_-]+\.js)(?:\?|$)/);
      if (match?.[1]) return match[1];
    }
  } catch {
    /* ignore */
  }
  return '';
}

export interface RemoteVersionInfo {
  version?: string;
  sha?: string;
  frontend_git_sha?: string;
  gitHash?: string;
  buildTimestamp?: number;
  bundle_hash?: string;
}

function readStampedWebVersion(): string {
  if (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) return __APP_VERSION__;
  try {
    const fromGlobal = (globalThis as { __APP_VERSION__?: string }).__APP_VERSION__;
    if (typeof fromGlobal === 'string' && fromGlobal.trim()) return fromGlobal.trim();
  } catch {
    /* ignore */
  }
  return 'unknown';
}

export function getWebBundleMeta(): WebBundleMeta {
  const version = readStampedWebVersion();
  const sha =
    typeof __GIT_SHA__ !== 'undefined' && __GIT_SHA__
      ? __GIT_SHA__
      : typeof __GIT_HASH__ !== 'undefined'
        ? __GIT_HASH__
        : '';
  const gitHash = typeof __GIT_HASH__ !== 'undefined' ? __GIT_HASH__ : sha.slice(0, 7);
  const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'unknown';
  const bundleHash = readEmbeddedBundleHash();
  return { version, sha, gitHash, buildTime, bundleHash };
}

/** True when embedded and remote SHAs refer to the same build. */
export function bundleShaMatches(embeddedSha: string, remoteSha: string | undefined): boolean {
  if (!embeddedSha || !remoteSha) return true;
  const e = embeddedSha.trim().toLowerCase();
  const r = remoteSha.trim().toLowerCase();
  if (!e || !r) return true;
  return e === r || e.startsWith(r) || r.startsWith(e);
}

export async function fetchRemoteVersion(): Promise<RemoteVersionInfo | null> {
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    return (await res.json()) as RemoteVersionInfo;
  } catch {
    return null;
  }
}

/** Playback is safe to interrupt for a silent bundle reload. */
export function isPlaybackStoppedForReload(): boolean {
  try {
    const state = useMaestroStore.getState().playbackState;
    if (state === 'playing' || state === 'buffering' || (state as string) === 'loading') {
      return false;
    }
  } catch {
    /* store unavailable — treat as stopped */
  }

  try {
    if (sessionStorage.getItem('active_ella_radio') === 'true') return false;
  } catch {
    /* ignore */
  }

  return true;
}

/** Cache-busted navigation — WKWebView location.reload() re-serves a stale shell. */
export function reloadWebBundle(): void {
  cacheBustedReload();
}
